/**
 * El botón ? de la cabecera.
 *
 * Existe por un problema medido: la ayuda estaba escrita, y bien escrita, pero sólo se llegaba a
 * ella desde Ajustes — y nadie entra a Ajustes a aprender a usar una app. Ahora vive en la
 * cabecera de todas las pantallas.
 *
 * Muestra SÓLO los temas de la pantalla donde estás. Un índice de siete temas obliga a buscar, y
 * quien está parado frente a una máquina no busca: abandona. El índice completo sigue existiendo
 * en Ajustes, y hay un pie que lleva ahí.
 *
 * Es la misma pieza en las dos variantes porque no es una pantalla: es un panel de texto. Lo único
 * que cambia es de dónde entra — hoja desde abajo en el celular, diálogo centrado en el PC.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { Icono } from './componentes.tsx';
import { PANTALLAS, useRuta } from './router.tsx';
import { temasDePantalla } from './Pista.tsx';

export function BotonAyuda({ variante }: { variante: 'mobile' | 'desktop' }): ReactNode {
  const { pantalla, ir } = useRuta();
  const [abierta, setAbierta] = useState(false);

  useEffect(() => {
    if (!abierta) return;
    const alTeclear = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setAbierta(false);
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [abierta]);

  const titulo = `Cómo funciona · ${PANTALLAS[pantalla].titulo}`;

  return (
    <>
      <button
        type="button"
        className="boton boton--chico boton--fantasma"
        onClick={() => setAbierta(true)}
        aria-label={`Ayuda: cómo funciona ${PANTALLAS[pantalla].titulo}`}
        title="Cómo funciona esta pantalla"
      >
        <Icono nombre="ayuda" tamano={18} />
        {/* En el PC la palabra va al lado del ícono: un círculo gris con un signo de pregunta se
            lee como decoración, no como un botón, y en la cabecera del escritorio sobra el ancho.
            En el celular no entra: ahí ya compiten el título, el sync y los ajustes. */}
        {variante === 'desktop' && 'Ayuda'}
      </button>

      {abierta && (
        <>
          <div className="m-hoja-fondo" onClick={() => setAbierta(false)} aria-hidden="true" />
          <div
            className={variante === 'mobile' ? 'm-hoja' : 'm-hoja hoja-centro'}
            role="dialog"
            aria-modal="true"
            aria-label={titulo}
          >
            <div className="fila fila--entre">
              <h2 className="titulo">{titulo}</h2>
              <button
                type="button"
                className="boton boton--chico boton--fantasma"
                onClick={() => setAbierta(false)}
                aria-label="Cerrar"
              >
                <Icono nombre="cerrar" tamano={18} />
              </button>
            </div>

            <div className="columna" style={{ gap: 'var(--e4)' }}>
              {temasDePantalla(pantalla).map((t) => (
                <div key={t.titulo}>
                  <strong style={{ fontSize: 14, display: 'block', marginBottom: 2 }}>{t.titulo}</strong>
                  <p className="nota">{t.cuerpo}</p>
                </div>
              ))}
            </div>

            {pantalla !== 'ajustes' && (
              <button
                type="button"
                className="boton boton--fantasma boton--chico"
                onClick={() => {
                  setAbierta(false);
                  ir('ajustes');
                }}
              >
                Ver toda la ayuda
              </button>
            )}
          </div>
        </>
      )}
    </>
  );
}
